// A theme-driven slider — the first control primitive magviz owns outright.
// Pure DOM + ./rangeMath; no `three`, no `@scene/*`, no UI-framework deps — so
// it lifts unchanged into webpic's @webpic/ui/controls.
//
// Single or interval (two grips; dragging the fill between them moves both
// ends). `step` quantizes drag + keyboard only — the coupled text field sets a
// raw value, so a typed number can rest between steps/ticks. The value↔track
// mapping is linear/log/symlog (symlog gives fine control near 0 for
// high-dynamic-range signed fields). On log/symlog, drag + keyboard snap to a
// log-decade grid instead (…, 10, 20, 50, 100, …; see rangeMath.snapToDecade) —
// the scientific "nice value" feel; text entry still bypasses it. Pointer Events
// throughout → touch and desktop share one path.
//
// Geometry convention: JS sets normalized fractions (`--t`/`--tlo`/`--thi`,
// fill `--fa`/`--fb`, tick `--mt`) in [0,1]; css/style.css insets them by half
// a grip width (`--gs`) so grips stay fully inside the track at the extremes.

import type { Disposer } from '../types';
import { clamp, clampInterval, makeScale, minorTickPositions, pointerT, snapToDecade, snapToStep, stepDecade, tickPositions, translateInterval } from './rangeMath';
import type { ScaleKind } from './rangeMath';

type Single = number;
type Pair = readonly [number, number];

export interface RangeControlOpts {
    min: number;
    max: number;
    /** Drag/keyboard granularity only; text entry bypasses it. Omit ⇒ continuous. */
    step?: number;
    /** Single-mode initial value (ignored when `range` is given). */
    value?: number;
    /** Presence selects interval mode: [lo, hi]. */
    range?: Pair;
    /** Subtle vertical ticks. `true` derives a count from `step`/scale. */
    ticks?: boolean | number;
    /** Lighter sub-decade minor ticks on log/symlog. Default on when `ticks`. */
    minorTicks?: boolean;
    /** Coupled numeric field(s) for precise entry. Default true. */
    text?: boolean;
    /** Value→string for the text field/aria (e.g. panel/numberFormat:rangeFormat). */
    format?: (v: number) => string;
    /** Position↔value mapping. Default 'linear'. */
    scale?: ScaleKind;
    /** symlog linear half-width around 0. */
    linthresh?: number;
    /** Single-mode fill anchor (default min; set 0 for a bipolar field). */
    origin?: number;
    /** Interval minimum gap (default = step ?? 0). */
    minGap?: number;
    /** Live, during drag/keys. */
    onInput?: (v: Single | Pair) => void;
    /** Commit, on pointer release / text change. */
    onChange?: (v: Single | Pair) => void;
}

export interface RangeControlHandle {
    readonly el: HTMLElement;
    /** Reflect an external (store) change without firing callbacks. */
    set(v: Single | Pair): void;
    dispose: Disposer;
}

function div(className: string): HTMLDivElement {
    const d = document.createElement('div');
    d.className = className;
    return d;
}

export function createRangeControl(opts: RangeControlOpts): RangeControlHandle {
    const { min, max } = opts;
    const step = opts.step;
    const isInterval = opts.range !== undefined;
    const scale = makeScale(opts.scale ?? 'linear', min, max, opts.linthresh !== undefined ? { linthresh: opts.linthresh } : {});
    const fmt = opts.format ?? ((v: number) => String(v));
    const origin = clamp(opts.origin ?? min, min, max);
    const minGap = opts.minGap ?? (step && step > 0 ? step : 0);
    const kbStep = step && step > 0 ? step : (max - min) / 100;

    // On log/symlog, drag + keyboard snap to a log-decade grid (…, 10, 20, 50,
    // 100, …) rather than the uniform `step`. `decadeMinStep` floors it one decade
    // below the symlog linear band (linthresh) so it can't subdivide forever
    // toward 0; log (min > 0) needs no floor. Linear sliders keep uniform `step`.
    const isLogish = scale.kind === 'log' || scale.kind === 'symlog';
    const decadeMinStep = scale.kind === 'symlog' && scale.linthresh > 0
        ? 10 ** (Math.floor(Math.log10(scale.linthresh) + 1e-9) - 1)
        : 0;
    const snapDrag = (raw: number): number =>
        isLogish ? snapToDecade(raw, min, max, decadeMinStep) : snapToStep(raw, min, max, step);
    const snapStep = (raw: number): number => snapToStep(raw, min, max, step);
    const stepN = (cur: number, dir: 1 | -1, n: number): number => {
        let v = cur;
        for (let i = 0; i < n; i++) v = stepDecade(v, dir, min, max, decadeMinStep);
        return v;
    };

    let value = clamp(opts.value ?? min, min, max);
    let [lo, hi] = isInterval ? clampInterval(opts.range![0], opts.range![1], min, max, minGap) : [min, max];

    const root = div('mv-slider');
    root.dataset['mode'] = isInterval ? 'interval' : 'single';
    const track = div('mv-slider-track');
    const fill = div('mv-slider-fill');
    root.appendChild(track);

    track.appendChild(fill);

    // Tick marks paint above the fill (subtle, low-alpha) so they stay visible
    // over the colored range. Optional evenly/decade-spaced ticks, plus an
    // always-on tick at the fill origin when it sits in the interior — e.g. 0
    // for a bipolar B-field slider — marking the zero crossing.
    const tickTs: number[] = [];
    if (opts.ticks) {
        const count = typeof opts.ticks === 'number' ? opts.ticks : undefined;
        tickTs.push(...tickPositions(scale, {
            ...(step !== undefined ? { step } : {}),
            ...(count !== undefined ? { count } : {}),
        }));
    }
    if (!isInterval && origin > min && origin < max) {
        const ot = scale.toT(origin);
        if (!tickTs.some((t) => Math.abs(t - ot) < 1e-6)) tickTs.push(ot);
    }
    // Sub-decade minor marks for log/symlog (the 2..9 within each decade), drawn
    // first so the brighter decade majors paint over any coincident position.
    const minorTs = (opts.ticks && opts.minorTicks !== false && isLogish)
        ? minorTickPositions(scale)
        : [];
    if (tickTs.length || minorTs.length) {
        const layer = div('mv-slider-ticks');
        for (const t of minorTs) {
            const mark = div('mv-slider-tick is-minor');
            mark.style.setProperty('--mt', String(t));
            layer.appendChild(mark);
        }
        for (const t of tickTs) {
            const mark = div('mv-slider-tick');
            mark.style.setProperty('--mt', String(t));
            layer.appendChild(mark);
        }
        track.appendChild(layer);
    }

    const makeGrip = (end: 'value' | 'lo' | 'hi'): HTMLDivElement => {
        const g = div('mv-slider-grip');
        g.dataset['end'] = end;
        g.tabIndex = 0;
        g.setAttribute('role', 'slider');
        g.setAttribute('aria-orientation', 'horizontal');
        g.setAttribute('aria-valuemin', String(min));
        g.setAttribute('aria-valuemax', String(max));
        track.appendChild(g);
        return g;
    };
    const gripValue = isInterval ? null : makeGrip('value');
    const gripLo = isInterval ? makeGrip('lo') : null;
    const gripHi = isInterval ? makeGrip('hi') : null;

    let inputA: HTMLInputElement | null = null;
    let inputB: HTMLInputElement | null = null;
    if (opts.text !== false) {
        const wrap = div('mv-slider-text');
        const mkInput = (): HTMLInputElement => {
            const i = document.createElement('input');
            i.type = 'text';
            i.inputMode = 'decimal';
            i.spellcheck = false;
            wrap.appendChild(i);
            return i;
        };
        inputA = mkInput();
        if (isInterval) inputB = mkInput();
        root.appendChild(wrap);
    }

    const setGripAria = (g: HTMLElement | null, v: number): void => {
        if (!g) return;
        g.setAttribute('aria-valuenow', String(v));
        g.setAttribute('aria-valuetext', fmt(v));
    };

    function render(): void {
        if (isInterval) {
            track.style.setProperty('--tlo', String(scale.toT(lo)));
            track.style.setProperty('--thi', String(scale.toT(hi)));
            fill.style.setProperty('--fa', String(scale.toT(lo)));
            fill.style.setProperty('--fb', String(scale.toT(hi)));
            setGripAria(gripLo, lo);
            setGripAria(gripHi, hi);
            if (inputA && document.activeElement !== inputA) inputA.value = fmt(lo);
            if (inputB && document.activeElement !== inputB) inputB.value = fmt(hi);
        } else {
            const tv = scale.toT(value);
            const t0 = scale.toT(origin);
            track.style.setProperty('--t', String(tv));
            fill.style.setProperty('--fa', String(Math.min(tv, t0)));
            fill.style.setProperty('--fb', String(Math.max(tv, t0)));
            // Square the fill's edge at an interior origin (the 0 baseline) so the
            // bar reads as anchored there; its moving (grip) end stays rounded.
            const interior = origin > min && origin < max;
            if (interior && value !== origin) fill.dataset['origin'] = value > origin ? 'left' : 'right';
            else delete fill.dataset['origin'];
            setGripAria(gripValue, value);
            if (inputA && document.activeElement !== inputA) inputA.value = fmt(value);
        }
    }

    const emit = (cb?: (v: Single | Pair) => void): void => {
        if (!cb) return;
        cb(isInterval ? [lo, hi] : value);
    };

    // --- drag ---
    type Drag = { kind: 'value' | 'lo' | 'hi' | 'pan'; baseLo: number; baseHi: number; anchor: number };
    let drag: Drag | null = null;

    const valueAt = (clientX: number): number => scale.toValue(pointerT(clientX, track.getBoundingClientRect()));

    const applyGrip = (kind: 'value' | 'lo' | 'hi', raw: number, snap: (r: number) => number = snapDrag): void => {
        const v = snap(raw);
        if (kind === 'value') value = v;
        else if (kind === 'lo') lo = clamp(v, min, Math.max(min, hi - minGap));
        else hi = clamp(v, Math.min(max, lo + minGap), max);
    };

    const onDown = (e: PointerEvent): void => {
        if (e.button > 0) return; // left/touch/pen only
        const raw = valueAt(e.clientX);
        const base = { baseLo: lo, baseHi: hi, anchor: raw };
        if (isInterval) {
            // Intent from pointer position, not hit target, so the whole tall hit
            // band works: grab a grip → drag it; outside [lo,hi] → extend the
            // nearer end; between the grips → pan both together.
            if (e.target === gripLo) {
                drag = { kind: 'lo', ...base };
            } else if (e.target === gripHi) {
                drag = { kind: 'hi', ...base };
            } else if (raw <= lo) {
                applyGrip('lo', raw);
                drag = { kind: 'lo', ...base };
                gripLo?.focus();
            } else if (raw >= hi) {
                applyGrip('hi', raw);
                drag = { kind: 'hi', ...base };
                gripHi?.focus();
            } else {
                drag = { kind: 'pan', ...base };
            }
        } else {
            applyGrip('value', raw);
            drag = { kind: 'value', ...base };
            gripValue?.focus();
        }
        track.setPointerCapture(e.pointerId);
        root.classList.add('is-dragging');
        render();
        emit(opts.onInput);
        e.preventDefault();
    };

    const onMove = (e: PointerEvent): void => {
        if (!drag) return;
        if (drag.kind === 'pan') {
            const rawDelta = valueAt(e.clientX) - drag.anchor;
            const delta = step && step > 0 ? Math.round(rawDelta / step) * step : rawDelta;
            [lo, hi] = translateInterval(drag.baseLo, drag.baseHi, delta, min, max);
        } else {
            applyGrip(drag.kind, valueAt(e.clientX));
        }
        render();
        emit(opts.onInput);
    };

    const onUp = (e: PointerEvent): void => {
        if (!drag) return;
        try { track.releasePointerCapture(e.pointerId); } catch { /* already released */ }
        drag = null;
        root.classList.remove('is-dragging');
        emit(opts.onChange);
    };

    // --- keyboard (grips) ---
    const onKey = (e: KeyboardEvent): void => {
        const end = (e.currentTarget as HTMLElement).dataset['end'] as 'value' | 'lo' | 'hi';
        const cur = end === 'lo' ? lo : end === 'hi' ? hi : value;
        // On log/symlog the value grip walks the decade grid (Shift = 10 cells);
        // interval ends and linear sliders keep the fine `step` nudge.
        const decade = isLogish && end === 'value';
        const mult = e.shiftKey ? 10 : 1;
        let next: number | null = null;
        switch (e.key) {
            case 'ArrowLeft': case 'ArrowDown': next = decade ? stepN(cur, -1, mult) : cur - kbStep * mult; break;
            case 'ArrowRight': case 'ArrowUp': next = decade ? stepN(cur, 1, mult) : cur + kbStep * mult; break;
            case 'Home': next = min; break;
            case 'End': next = max; break;
            default: return;
        }
        applyGrip(end, next, decade ? snapDrag : snapStep);
        render();
        emit(opts.onInput);
        emit(opts.onChange);
        e.preventDefault();
    };

    // --- text (precise, no snap) ---
    const onTextA = (): void => {
        const n = Number(inputA!.value);
        if (!Number.isFinite(n)) { render(); return; }
        if (isInterval) [lo, hi] = clampInterval(n, hi, min, max, minGap);
        else value = clamp(n, min, max);
        render();
        emit(opts.onChange);
    };
    const onTextB = (): void => {
        const n = Number(inputB!.value);
        if (!Number.isFinite(n)) { render(); return; }
        [lo, hi] = clampInterval(lo, n, min, max, minGap);
        render();
        emit(opts.onChange);
    };

    track.addEventListener('pointerdown', onDown);
    track.addEventListener('pointermove', onMove);
    track.addEventListener('pointerup', onUp);
    track.addEventListener('pointercancel', onUp);
    for (const g of [gripValue, gripLo, gripHi]) g?.addEventListener('keydown', onKey);
    inputA?.addEventListener('change', onTextA);
    inputB?.addEventListener('change', onTextB);

    render();

    return {
        el: root,
        set(v) {
            if (isInterval && Array.isArray(v)) [lo, hi] = clampInterval(v[0], v[1], min, max, minGap);
            else if (!isInterval && typeof v === 'number') value = clamp(v, min, max);
            render();
        },
        dispose() {
            track.removeEventListener('pointerdown', onDown);
            track.removeEventListener('pointermove', onMove);
            track.removeEventListener('pointerup', onUp);
            track.removeEventListener('pointercancel', onUp);
            for (const g of [gripValue, gripLo, gripHi]) g?.removeEventListener('keydown', onKey);
            inputA?.removeEventListener('change', onTextA);
            inputB?.removeEventListener('change', onTextB);
            root.remove();
        },
    };
}
